import React, { createContext, useEffect, useMemo, useContext, useCallback } from 'react';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useParams, useLocation } from 'react-router-dom';
import { camelizeKeys, decamelizeKeys } from 'humps';

import { Question } from '../models';
import { useAuth } from '../../authentication';
import { useOrganizations } from '../../organization/providers';
import { useGetAssetsQuery } from '../hooks/useGetAssetsQuery';
import { useGetQuestionsQuery } from '../hooks/useGetQuestionsQuery';
import { useGetQuestionVersions } from '../hooks/useGetQuestionVersions';
import { useCreateQuestionMutation } from '../hooks/useCreateQuestionMutation';
import { useUpdateQuestionMutation } from '../hooks/useUpdateQuestionMutation';
import { useDeleteQuestionMutation } from '../hooks/useDeleteQuestionMutation';
import { useRetryQuestionMutation } from '../hooks/useRetryQuestionMutation';
import { useAddQuestionToSpaceMutation } from '../hooks/useAddQuestionToSpaceMutation';
import { useRemoveQuestionFromSpaceMutation } from '../hooks/useRemoveQuestionFromSpaceMutation';
import { useNotifications } from '../../../providers';
import { useConversations } from './ConversationsProvider';
import { useAgenticConversations } from './AgenticConversationsProvider';
import { useSocket, useApiClient } from '../../../hooks';
import { useCreateAnswerMutation } from '../hooks/useCreateAnswerMutation';

const QuestionsContext = createContext();

const addFollowUp = (questions, questionId, followUp) => {
  const addFollowUpToQuestion = (questionList) =>
    questionList.map((question) => {
      if (question.id === questionId) {
        return {
          ...question,
          followUps: [...(question.followUps ?? []), followUp]
        };
      }

      if (question.followUps) {
        return {
          ...question,
          followUps: addFollowUpToQuestion(question.followUps) // Recursive call on follow-ups
        };
      }
      return question;
    });

  return addFollowUpToQuestion(questions);
};

const setQueryData = (queryClient, queryKey, question, previousQuestionId) => {
  if (previousQuestionId) {
    return queryClient.setQueryData(queryKey, (old) =>
      addFollowUp(old, previousQuestionId, question)
    );
  }

  return queryClient.setQueryData(queryKey, (old) => [
    ...(old ?? []).filter((q) => q.id !== question.id),
    question
  ]);
};

const deleteQ = (questions, questionId) => {
  const recursivelyDeleteQuestion = (questionList) =>
    questionList
      .map((question) => {
        if (question.id === questionId) {
          return null; // Mark question for deletion
        }
        if (question.followUps) {
          const updatedFollowUps = recursivelyDeleteQuestion(question.followUps).filter(Boolean);
          return {
            ...question,
            followUps: updatedFollowUps
          };
        }
        return question;
      })
      .filter(Boolean); // Remove null entries (deleted questions)

  return recursivelyDeleteQuestion(questions);
};

// Helper functions extracted outside of the callback
const resetAnswersRecursive = (questions, targetId = null) =>
  questions.map((q) => {
    if (q.id === targetId) return { ...q, answers: [] };
    return q.followUps ? { ...q, followUps: resetAnswersRecursive(q.followUps, targetId) } : q;
  });

const updateQuestionAnswers = (questions, targetId, updatedAnswer = []) =>
  questions.map((q) => {
    if (q.id === targetId) {
      return { ...q, answers: [updatedAnswer] };
    }
    return q.followUps
      ? { ...q, followUps: updateQuestionAnswers(q.followUps, targetId, updatedAnswer) }
      : q;
  });

const findQuestionRecursively = (questions, targetId) => {
  if (!questions?.length) return null;

  // Try to find direct match
  const directMatch = questions.find((q) => q.id === targetId);
  if (directMatch) return directMatch;

  // Look in followUps recursively
  let result = null;
  questions.some((q) => {
    if (q.followUps?.length) {
      result = findQuestionRecursively(q.followUps, targetId);
      return !!result;
    }
    return false;
  });

  return result;
};

const QuestionsProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const { addNotification } = useNotifications();
  const location = useLocation();
  const { conversationId, spaceId } = useParams();

  // Always call hooks (React hook rules)
  const regularConversations = useConversations();
  useAgenticConversations(); // Called to satisfy React Hook rules

  // Conditionally use the appropriate conversations context based on route
  const isAgentic = location.pathname.startsWith('/agentic-chats');

  // Get current conversation ID from appropriate source
  const currentConversationId = isAgentic
    ? conversationId
    : regularConversations?.currentConversation?.id;

  // Create a unified currentConversation object for backward compatibility
  const currentConversation = isAgentic
    ? { id: conversationId }
    : regularConversations?.currentConversation;

  const queryClient = useQueryClient();
  const questionsQuery = useGetQuestionsQuery(
    accessToken,
    currentOrganization?.id,
    currentConversationId
  );

  const apiClient = useApiClient(accessToken);
  const assetsQuery = useGetAssetsQuery(accessToken, currentOrganization?.id, spaceId);

  // Get asset IDs for version loading
  const assetIds = useMemo(() => assetsQuery?.data?.map((asset) => asset.id), [assetsQuery.data]);

  // Compute the selected asset based on the URL hash
  const selectedAsset = useMemo(() => {
    const questionId = location.hash.slice(1); // Remove the '#' from the hash
    const rawQuestion = assetsQuery.data?.find((q) => q.id === questionId);
    return rawQuestion ? new Question(rawQuestion) : null;
  }, [assetsQuery.data, location.hash]);

  // Use infinite query for selected asset versions
  const selectedAssetVersionsQuery = useGetQuestionVersions(
    accessToken,
    selectedAsset?.id,
    currentOrganization?.id
  );

  // Use React Query's useQueries with the same pattern as useGetQuestionVersions hook
  const versionQueries = useQueries({
    queries: (assetIds || []).map((assetId) => ({
      queryKey: ['chat', 'questions', 'versions', assetId],
      queryFn: async () => {
        const { data } = await apiClient.get(`/chat/questions/${assetId}/versions`, {
          params: decamelizeKeys({ organizationId: currentOrganization?.id })
        });
        queryClient.setQueryData(
          ['chat', 'questions', 'assets', currentOrganization?.id, spaceId],
          (oldData) =>
            oldData.map((question) => {
              if (question.id === assetId) {
                const existingVersions = question.versions || [];
                const newVersions = data.data.map((version) => camelizeKeys(version));
                const existingIds = new Set([assetId, ...existingVersions.map((v) => v.id)]);

                return {
                  ...question,
                  versions: [
                    { ...question, versions: [] },
                    ...existingVersions,
                    ...newVersions.filter((v) => !existingIds.has(v.id))
                  ]
                };
              }
              return question;
            })
        );
        return camelizeKeys(data);
      },
      retry: false,
      enabled: Boolean(accessToken) && Boolean(assetId) && Boolean(currentOrganization?.id)
    }))
  });

  // Helper function to get versions for a specific question
  const getQuestionVersions = useCallback(
    (questionId) => {
      const index = assetIds?.indexOf(questionId);
      if (index === -1) return [];

      const query = versionQueries[index];
      if (query?.data?.data) {
        return query.data.data.map((version) => new Question(version));
      }
      return [];
    },
    [assetIds, versionQueries]
  );

  // Helper function to check if versions are loading for a specific question
  const isLoadingVersions = useCallback(
    (questionId) => {
      const index = assetIds?.indexOf(questionId);
      if (index === -1) return false;

      const query = versionQueries[index];
      return Boolean(query?.isLoading || query?.isFetching);
    },
    [assetIds, versionQueries]
  );
  const createQuestionMutation = useCreateQuestionMutation(accessToken);
  const updateQuestionMutation = useUpdateQuestionMutation(accessToken);
  const deleteQuestionMutation = useDeleteQuestionMutation(accessToken);
  const retryQuestionMutation = useRetryQuestionMutation(accessToken);
  const createAnswerMutation = useCreateAnswerMutation(accessToken);

  const addQuestionToSpaceMutation = useAddQuestionToSpaceMutation(
    accessToken,
    currentOrganization?.id
  );

  const removeQuestionfromSpaceMutation = useRemoveQuestionFromSpaceMutation(
    accessToken,
    currentOrganization?.id
  );

  const { io, joinRoom, leaveRoom } = useSocket(accessToken, currentOrganization?.id, 'chat');

  // Helper functions for updating assets (similar to AssetBuilderProvider)
  const updateAssetParams = (assets, questionId, params) =>
    assets?.map((asset) => {
      if (asset.id === questionId) {
        return {
          ...asset,
          ...params
        };
      }
      return asset;
    });

  // Define websocket handlers as useCallback hooks (matching AssetBuilderProvider pattern)
  const handlePipelineStepComplete = useCallback(
    (data) => {
      if (!spaceId) return;
      const queryKey = ['chat', 'questions', 'assets', currentOrganization?.id, spaceId];
      const { questionId, isRetry, pipelineStep, answer } = camelizeKeys(data);

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return oldData;
        return updateAssetParams(oldData, questionId, {
          status: pipelineStep,
          isRetry,
          answers: answer ? [answer] : []
        });
      });
    },
    [queryClient, currentOrganization?.id, spaceId]
  );

  const handleQuestionAnswered = useCallback(
    (data) => {
      if (!spaceId) return;

      const queryKey = ['chat', 'questions', 'assets', currentOrganization?.id, spaceId];
      const { questionVersionsIds, question } = camelizeKeys(data);

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return oldData;

        return oldData.map((existingQuestion) => {
          // Try both the question.id and questionVersionsIds approach
          if (
            questionVersionsIds.includes(existingQuestion.id) ||
            existingQuestion.id === question.id
          ) {
            // Failed answers: Store a new version of the question AND update the main question with the failed answer
            if (question.answers[0]?.error) {
              return {
                ...question, // Use the new question data (including failed answer)
                order: existingQuestion.order, // Preserve order
                versions: [...(existingQuestion.versions || []), existingQuestion] // Store the old question as a version
              };
            }

            // Successful answers: Completely replace the question but preserve order
            return {
              ...question,
              order: existingQuestion.order,
              versions: [existingQuestion]
            };
          }

          return existingQuestion;
        });
      });
    },
    [queryClient, currentOrganization?.id, spaceId]
  );

  // Websocket event listeners
  useEffect(() => {
    if (io) {
      io.on('question_answered', handleQuestionAnswered);
      io.on('pipeline_step_complete', handlePipelineStepComplete);
    }

    return () => {
      if (io) {
        io.off('question_answered', handleQuestionAnswered);
        io.off('pipeline_step_complete', handlePipelineStepComplete);
      }
    };
  }, [io, handleQuestionAnswered, handlePipelineStepComplete]);

  // Room management
  useEffect(() => {
    if (spaceId && joinRoom) {
      joinRoom('space', spaceId);
    }

    return () => {
      if (spaceId && leaveRoom) {
        leaveRoom('space', spaceId);
      }
    };
  }, [joinRoom, leaveRoom, spaceId]);

  const createQuestion = useCallback(
    async (
      body,
      targetConversationId = null,
      previousQuestionId = null,
      command = null,
      matchingEntities = null
    ) => {
      const effectiveConversationId = targetConversationId || currentConversation?.id;
      const queryKey = ['chat', 'questions', currentOrganization?.id, effectiveConversationId];
      const previousState = await queryClient.getQueryData(queryKey);

      try {
        const response = await createQuestionMutation.mutateAsync({
          body,
          previousQuestionId,
          organizationId: currentOrganization?.id,
          conversationId: effectiveConversationId,
          ...(command && { command }),
          ...(matchingEntities && { matchedEntities: matchingEntities })
        });

        const question = camelizeKeys(response.data);

        setQueryData(queryClient, queryKey, question, previousQuestionId);
        return question;
      } catch (err) {
        queryClient.setQueryData(queryKey, () => previousState);
        return null;
      }
    },
    [createQuestionMutation, questionsQuery.data, currentOrganization, currentConversation]
  );

  const updateAssetsCache = useCallback(
    (questionId, data) => {
      if (!spaceId) return;

      const spaceQueryKey = ['chat', 'questions', 'assets', currentOrganization?.id, spaceId];
      const updater = (old) => {
        if (!old) return old;
        return old.map((question) =>
          question.id === questionId
            ? {
                ...question,
                ...data.body
              }
            : question
        );
      };

      queryClient.setQueryData(spaceQueryKey, updater);
    },
    [queryClient, currentOrganization, spaceId]
  );

  const updateQuestion = useCallback(
    async (data, questionId) => {
      const queryKey = ['chat', 'questions', currentOrganization?.id, currentConversation?.id];
      const previousState = await queryClient.getQueryData(queryKey);

      try {
        await updateQuestionMutation.mutateAsync({
          questionId,
          organizationId: currentOrganization?.id,
          data
        });

        const updater = (old) => {
          if (!old) return old;
          return old.map((question) =>
            question.id === questionId
              ? {
                  ...question,
                  ...data.body
                }
              : question
          );
        };

        queryClient.setQueryData(queryKey, updater);

        // Update assets cache
        updateAssetsCache(questionId, data);
      } catch (err) {
        queryClient.setQueryData(queryKey, () => previousState);
      }
      return null;
    },
    [
      updateQuestionMutation,
      queryClient,
      currentOrganization,
      currentConversation,
      spaceId,
      updateAssetsCache
    ]
  );

  const deleteQuestion = useCallback(
    async (questionId) => {
      const questionsQueryKey = [
        'chat',
        'questions',
        currentOrganization?.id,
        currentConversation?.id
      ];

      const queryKey = questionsQueryKey;
      const previousState = await queryClient.getQueryData(questionsQueryKey);

      try {
        await deleteQuestionMutation.mutateAsync({
          questionId,
          organizationId: currentOrganization?.id
        });

        queryClient.setQueryData(queryKey, (old) => deleteQ(old, questionId));
      } catch (err) {
        addNotification({
          icon: 'alert',
          message: `Couldn't delete the question`
        });
        queryClient.setQueryData(queryKey, () => previousState);
      }
    },
    [deleteQuestionMutation, queryClient, currentOrganization, addNotification]
  );

  const retryAssetQuestion = useCallback(
    async (questionId) => {
      const assetsQueryKey = ['chat', 'questions', 'assets', currentOrganization?.id, spaceId];

      // Store the original state before making optimistic update
      const previousState = queryClient.getQueryData(assetsQueryKey);

      try {
        queryClient.setQueryData(assetsQueryKey, (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((q) => {
            if (q.id === questionId) {
              return {
                ...q,
                answers: []
              };
            }
            return q;
          });
        });

        // Step 2: Perform mutation
        await retryQuestionMutation.mutateAsync({
          organizationId: currentOrganization?.id,
          questionId
        });

        return null;
      } catch (err) {
        addNotification({
          icon: 'alert',
          message: `Something went wrong`
        });
        // Revert to the original state
        queryClient.setQueryData(assetsQueryKey, () => previousState);
        return null;
      }
    },
    [retryQuestionMutation, currentOrganization, spaceId, queryClient, addNotification]
  );

  const retryChatQuestion = useCallback(
    async (questionId) => {
      const questionsQueryKey = [
        'chat',
        'questions',
        currentOrganization?.id,
        currentConversation?.id
      ];

      try {
        // Helper function to reset answers in a question, handling nested followUps
        const resetAnswers = (questions, _questionId) => {
          const recursivelyResetAnswers = (questionList) =>
            questionList?.map((q) => {
              if (q.id === _questionId) {
                return {
                  ...q,
                  answers: []
                };
              }
              if (q.followUps) {
                return {
                  ...q,
                  followUps: recursivelyResetAnswers(q.followUps)
                };
              }
              return q;
            });

          return recursivelyResetAnswers(questions);
        };

        // Helper function to update question retry status, handling nested followUps
        const updateQuestionRetryStatus = (questions, _questionId, updatedQuestionData) => {
          const recursivelyUpdateQuestion = (questionList) =>
            questionList?.map((q) => {
              if (q.id === _questionId) {
                return {
                  ...q,
                  status: updatedQuestionData.status,
                  isRetry: true,
                  answers: [] // Keep answers cleared until websocket provides them
                };
              }
              if (q.followUps) {
                return {
                  ...q,
                  followUps: recursivelyUpdateQuestion(q.followUps)
                };
              }
              return q;
            });

          return recursivelyUpdateQuestion(questions);
        };

        // Step 1: Reset answers before mutation
        queryClient.setQueryData(questionsQueryKey, (oldQuestions) =>
          resetAnswers(oldQuestions, questionId)
        );

        // Step 2: Perform mutation
        const { data: updatedQuestion } = await retryQuestionMutation.mutateAsync({
          organizationId: currentOrganization?.id,
          questionId
        });

        // Step 3: Update questions cache with retry status only - let websocket handlers provide the final answer
        queryClient.setQueryData(questionsQueryKey, (oldQuestions) =>
          updateQuestionRetryStatus(oldQuestions, questionId, updatedQuestion)
        );

        return new Question(updatedQuestion);
      } catch (err) {
        addNotification({
          icon: 'alert',
          message: `Something went wrong`
        });

        return null;
      }
    },
    [retryQuestionMutation, currentOrganization, currentConversation, queryClient, addNotification]
  );

  const updateQuestionAnswer = useCallback(
    (answer) => {
      const questionsQueryKey = [
        'chat',
        'questions',
        currentOrganization?.id,
        currentConversation?.id
      ];
      const assetsQueryKey = ['chat', 'questions', 'assets', currentOrganization?.id, spaceId];
      // Update assets query data
      queryClient.setQueryData(assetsQueryKey, (oldData) => {
        if (!oldData) return oldData;

        const updateAnswerRecursively = (questions) =>
          questions?.map((q) => {
            // Update answer if found in current question
            if (q.id === answer.questionId) {
              return {
                ...q,
                answers: q.answers.map((a) => (a.id === answer.id ? answer : a))
              };
            }

            // Recursively check followUps if they exist
            if (q.followUps) {
              return {
                ...q,
                followUps: updateAnswerRecursively(q.followUps)
              };
            }

            return q;
          });

        return updateAnswerRecursively(oldData);
      });

      // Update questions query data
      return queryClient.setQueryData(questionsQueryKey, (oldQuestions) => {
        const updateAnswerRecursively = (questions) =>
          questions?.map((q) => {
            // Update answer if found in current question
            if (q.id === answer.questionId) {
              return {
                ...q,
                answers: q.answers.map((a) => (a.id === answer.id ? answer : a))
              };
            }

            // Recursively check followUps if they exist
            if (q.followUps) {
              return {
                ...q,
                followUps: updateAnswerRecursively(q.followUps)
              };
            }

            return q;
          });

        return updateAnswerRecursively(oldQuestions);
      });
    },
    [queryClient, currentOrganization, currentConversation, spaceId]
  );

  const addQuestionToSpace = useCallback(
    async (question, space, revert = false) => {
      // question is an instance of the Question model
      try {
        const queryKey = ['chat', 'questions', 'assets', currentOrganization?.id, spaceId];
        const questionsQueryKey = [
          'chat',
          'questions',
          currentOrganization?.id,
          currentConversation?.id
        ];

        await addQuestionToSpaceMutation.mutateAsync({
          spaceId: space.id,
          questionId: question.id
        });

        // Update space query data
        queryClient.setQueryData(queryKey, (oldData) => {
          if (!oldData) return oldData;

          // check if the question is already in the space
          if (oldData.some((q) => q.id === question.id && q.deletedAt === null)) {
            return oldData;
          }

          return [question.toJS(), ...oldData];
        });

        // Update questions query data
        queryClient.setQueryData(questionsQueryKey, (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((q) => {
            if (q.id === question.id) {
              return {
                ...q,
                spaceIds: [...(q.spaceIds || []), space.id]
              };
            }
            return q;
          });
        });

        if (revert) return;

        addNotification({
          icon: 'pin',
          message: `Added ${question.assetTitle ?? 'asset'} to ${space.title} space`,
          highlight: question.assetTitle
            ? [space.title, question.assetTitle]
            : [space.title, 'asset'],
          undo: () => removeQuestionFromSpace(question, space, true)
        });
      } catch (err) {
        addNotification({
          icon: 'alert',
          message: `Couldn't add the question to the space`
        });
      }
    },
    [addQuestionToSpaceMutation, addNotification]
  );

  const removeQuestionFromSpace = useCallback(
    async (question, space, revert = false) => {
      try {
        const queryKey = ['chat', 'questions', 'assets', currentOrganization?.id, spaceId];
        const questionsQueryKey = [
          'chat',
          'questions',
          currentOrganization?.id,
          currentConversation?.id
        ];

        await removeQuestionfromSpaceMutation.mutateAsync({
          spaceId: space.id,
          questionId: question.id
        });

        // Update space query data
        queryClient.setQueryData(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter((q) => q.id !== question.id);
        });

        // Update questions query data
        queryClient.setQueryData(questionsQueryKey, (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((q) => {
            if (q.id === question.id) {
              return {
                ...q,
                spaceIds: [...(q.spaceIds || [])].filter((id) => id !== space.id)
              };
            }
            return q;
          });
        });

        if (revert) return;

        addNotification({
          icon: 'trash',
          message: `Removed ${question.assetTitle ?? 'asset'} from ${space.title} space`,
          highlight: question.assetTitle
            ? [space.title, question.assetTitle]
            : [space.title, 'asset'],
          undo: () => addQuestionToSpace(question, space, true)
        });
      } catch (err) {
        addNotification({
          icon: 'alert',
          message: `Couldn't remove the question from the space`
        });
      }
    },
    [removeQuestionfromSpaceMutation, addNotification]
  );

  const createAnswer = useCallback(
    async (questionId, rawSql) => {
      // Build your cache key once
      const questionsQueryKey = [
        'chat',
        'questions',
        currentOrganization?.id,
        currentConversation?.id
      ];

      // Optimistically clear existing answers
      queryClient.setQueryData(questionsQueryKey, (old) => resetAnswersRecursive(old, questionId));

      try {
        // Perform the mutation
        const { data: updatedAnswer } = await createAnswerMutation.mutateAsync({
          organizationId: currentOrganization?.id,
          questionId,
          rawSql
        });

        // Update cache with the new answer
        queryClient.setQueryData(questionsQueryKey, (old) =>
          updateQuestionAnswers(old, questionId, updatedAnswer)
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('createAnswer failed', err);
      }

      // return for both success and failure
      const questions = queryClient.getQueryData(questionsQueryKey);
      return findQuestionRecursively(questions, questionId);
    },
    [createAnswerMutation, queryClient, currentOrganization?.id, currentConversation?.id]
  );

  const value = useMemo(
    () => ({
      assets: assetsQuery?.data?.map((question) => new Question(question)),
      refetchAssets: assetsQuery.refetch,
      questions:
        questionsQuery.data
          ?.map((question) => new Question(question))
          .filter((question) => question.deletedAt === null) ?? [],
      createQuestion,
      updateQuestion,
      deleteQuestion,
      updateQuestionAnswer,
      retryAssetQuestion,
      retryChatQuestion,
      createAnswer,
      isFetchingQuestions: questionsQuery.isFetching,
      isFetchingAssets: assetsQuery.isFetching,
      isRefetchingAssets: assetsQuery.isRefetching,
      addQuestionToSpace,
      removeQuestionFromSpace,
      spaceId,
      getQuestionVersions,
      isLoadingVersions,
      selectedAsset,
      selectedAssetVersionsQuery
    }),
    [
      questionsQuery.data,
      assetsQuery?.data,
      assetsQuery.isRefetching,
      questionsQuery.isFetching,
      assetsQuery.isFetching,
      spaceId,
      addQuestionToSpace,
      removeQuestionFromSpace,
      createAnswer,
      getQuestionVersions,
      isLoadingVersions,
      selectedAsset,
      selectedAssetVersionsQuery,
      retryAssetQuestion,
      retryChatQuestion
    ]
  );

  return <QuestionsContext.Provider value={value}>{children}</QuestionsContext.Provider>;
};

QuestionsProvider.propTypes = {
  children: PropTypes.node
};

const useQuestions = () => useContext(QuestionsContext);

export { QuestionsProvider, QuestionsContext, useQuestions };
